import {
  pickAttainmentTrendMaxPeriod,
  type AttainmentTrendPeriod,
} from "@repo/app-types/entities/attainmentTrend";
import {
  buildOutcomeGreenRateLabel,
  getOutcomeNoun,
} from "@repo/app-types/entities/outcome";
import {
  asPlaybookId,
  collectPlaybookCriteria,
  getOutcomeDefinitionFromPlaybook,
} from "@repo/app-types/entities/playbook";
import {
  buildTeammateDisplayNames,
  type Teammate,
  type TeammateId,
} from "@repo/app-types/entities/teammate";
import { formatCallLengthHoursMinutes } from "@repo/app-types/helpers/formatCallLength";
import { findCriterionInSubtree } from "@repo/app-types/helpers/gradingAssessmentHelpers";
import { Size } from "@repo/ui/helpers/helpers";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@repo/ui/Select/simpleSelect";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState, type FC } from "react";
import { Heading } from "react-aria-components";
import { useParams } from "react-router";
import { twMerge } from "tailwind-merge";
import { usePlaybookByIdQuery } from "../../../api/entities/playbook.js";
import { useAllTeammatesQuery } from "../../../api/entities/teammate.js";
import { appPaths } from "../../../app/appPaths.js";
import { PageBody, PageLayout } from "../../../components/layout/pageLayout.js";
import { MetricAndTrendCard } from "../../../components/metricAndTrendCard.js";
import { PerformancePlaybookSwitcher } from "../../../components/performancePlaybookSwitcher.js";
import { useFilters } from "../../filters/filters.js";
import { HighlightMomentCard } from "../monthInReview/highlightMomentCard.js";
import { DashboardCriteriaAttainmentCard } from "./dashboardCriteriaAttainmentCard.js";
import { DashboardOutcomeAttainmentTrendCard } from "./dashboardOutcomeAttainmentTrendCard.js";
import { DashboardPlaybookAttainmentTrendCard } from "./dashboardPlaybookAttainmentTrendCard.js";
import {
  useLast30DaysDashboardData,
  useMonthDashboardData,
} from "./useDashboardData.js";

export const PerformanceDashboard: FC = () => {
  const { playbookId: playbookIdParam } = useParams();
  if (!playbookIdParam) {
    throw new Error("Missing playbookId param.");
  }
  const playbookId = asPlaybookId(playbookIdParam);

  const [{ teammateGroupIds }, renderFilters] = useFilters([
    "teammateGroupIds",
  ]);

  const { data: playbook } = usePlaybookByIdQuery(playbookId);
  const { data: teammates } = useAllTeammatesQuery();

  const [selectedPeriod, setSelectedPeriod] = useState<string | undefined>(
    undefined,
  );
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(
    null,
  );

  // Each attainment-trend card owns its own selected period in local state, but
  // notifies us so we can widen the ClickHouse query window when any card
  // shows a period longer than 30 days. We keep just the periods that exceed
  // 30 days (others map to the default `last_30_days` bucket anyway).
  const [longPeriodsByCard, setLongPeriodsByCard] = useState<
    ReadonlyMap<string, AttainmentTrendPeriod>
  >(() => new Map());
  const handleCardPeriodChange = useCallback(
    (cardKey: string, period: AttainmentTrendPeriod) => {
      setLongPeriodsByCard((prev) => {
        const wantsEntry =
          pickAttainmentTrendMaxPeriod([period]) === "last_180_days";
        const hasEntry = prev.has(cardKey);
        if (wantsEntry === hasEntry && prev.get(cardKey) === period) {
          return prev;
        }
        const next = new Map(prev);
        if (wantsEntry) {
          next.set(cardKey, period);
        } else {
          next.delete(cardKey);
        }
        return next;
      });
    },
    [],
  );
  const chartsMaxPeriod = useMemo(
    () => pickAttainmentTrendMaxPeriod(longPeriodsByCard.values()),
    [longPeriodsByCard],
  );

  const now = DateTime.now();

  // Build the period options: "Last 30 days", then the 12 preceding months
  const periodOptions = useMemo(
    (): readonly SimpleSelectOption<string | null>[] => [
      { value: null, label: "Last 30 days" },
      ...Array.from({ length: 12 }, (_, i) => ({
        label: now.minus({ months: i + 1 }).toFormat("MMM yyyy"),
        value: now.minus({ months: i + 1 }).toFormat("yyyy-MM"),
      })),
    ],
    // Stable per calendar day
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [now.toISODate()],
  );

  const teammatesMap = useMemo(() => {
    const map = new Map<TeammateId, Teammate>();
    for (const t of teammates ?? []) {
      map.set(t.id, t);
    }
    return map;
  }, [teammates]);

  const criteriaGradingValueTypes = useMemo(() => {
    const map = new Map<string, "yes_no" | "1_5_score">();
    if (!playbook?.assessment_tree) return map;
    for (const criterion of collectPlaybookCriteria(playbook.assessment_tree)) {
      map.set(criterion.id, criterion.grading_value_type);
    }
    return map;
  }, [playbook?.assessment_tree]);

  const outcomeDefinition = useMemo(
    () => getOutcomeDefinitionFromPlaybook(playbook),
    [playbook],
  );

  const outcomeGreenRateLabel = useMemo(
    () =>
      outcomeDefinition
        ? buildOutcomeGreenRateLabel(outcomeDefinition)
        : undefined,
    [outcomeDefinition],
  );

  const trendXAxisLabels = useMemo(() => {
    if (selectedPeriod === undefined) return undefined;
    const dt = DateTime.fromFormat(selectedPeriod, "yyyy-MM");
    const start = dt.toFormat("MMM") + " 1";
    const end = dt.endOf("month").toFormat("MMM d");
    return { start, end };
  }, [selectedPeriod]);

  const last30DaysData = useLast30DaysDashboardData({
    playbookId,
    groupIds: teammateGroupIds,
    sectionNodeId: selectedSectionId,
    enabled: selectedPeriod === undefined,
    criteriaGradingValueTypes,
    chartsMaxPeriod,
  });
  const monthData = useMonthDashboardData({
    playbookId,
    period: selectedPeriod,
    groupIds: teammateGroupIds,
    enabled: selectedPeriod !== undefined,
    criteriaGradingValueTypes,
  });
  const dashboardData =
    selectedPeriod === undefined ? last30DaysData : monthData;

  const comparisonLabel =
    selectedPeriod === undefined ? "last 30 days" : "last month";

  const periodSelector = (
    <SimpleSelect
      label="Period"
      isLabelHidden
      options={periodOptions}
      value={selectedPeriod ?? null}
      onChange={(value) => setSelectedPeriod(value ?? undefined)}
      size={Size.SMALL}
    />
  );

  return (
    <PageLayout>
      <PageHeader
        title="Overview"
        filterBar={renderFilters({
          leadingItems: <PerformancePlaybookSwitcher />,
          trailingItems: periodSelector,
        })}
      />

      <PageBody>
        {/* Headline metrics run off ClickHouse, not the monthly report, so
          they always render even when a report is missing for an old
          month. The "per green outcome" card only appears when the
          playbook has an outcome configured — the grid column count
          tracks that so we don't end up with an empty third cell. */}
        <div
          className={twMerge(
            "grid grid-cols-1 gap-4 sm:gap-6",
            outcomeDefinition ? "md:grid-cols-3" : "md:grid-cols-2",
          )}
        >
          <MetricAndTrendCard
            label="Call volume"
            currentValue={dashboardData.calls.currentTotalCount}
            previousValue={dashboardData.calls.previousTotalCount}
            trend={dashboardData.calls.trend}
            renderValue={(value) => value.toLocaleString()}
            isCountLoading={dashboardData.calls.isLoading}
            isDeltaLoading={dashboardData.calls.isLoading}
            isTrendLoading={dashboardData.calls.isLoading}
            comparisonLabel={comparisonLabel}
            xAxisLabels={trendXAxisLabels}
          />
          <MetricAndTrendCard
            label="Average call length"
            currentValue={
              dashboardData.callLength.currentAverageSeconds ?? undefined
            }
            previousValue={
              dashboardData.callLength.previousAverageSeconds ?? undefined
            }
            trend={dashboardData.callLength.trendSeconds}
            renderValue={formatCallLengthHoursMinutes}
            isCountLoading={dashboardData.callLength.isLoading}
            isDeltaLoading={dashboardData.callLength.isLoading}
            isTrendLoading={dashboardData.callLength.isLoading}
            comparisonLabel={comparisonLabel}
            xAxisLabels={trendXAxisLabels}
            inverseTone
            minYRange={60}
          />
          {outcomeDefinition && (
            <MetricAndTrendCard
              label={`Average time per ${getOutcomeNoun(outcomeDefinition).toLowerCase()}`}
              currentValue={
                dashboardData.callLengthPerGreenOutcome.currentAverageSeconds ??
                undefined
              }
              previousValue={
                dashboardData.callLengthPerGreenOutcome
                  .previousAverageSeconds ?? undefined
              }
              trend={dashboardData.callLengthPerGreenOutcome.trendSeconds}
              renderValue={formatCallLengthHoursMinutes}
              isCountLoading={dashboardData.callLengthPerGreenOutcome.isLoading}
              isDeltaLoading={dashboardData.callLengthPerGreenOutcome.isLoading}
              isTrendLoading={dashboardData.callLengthPerGreenOutcome.isLoading}
              comparisonLabel={comparisonLabel}
              xAxisLabels={trendXAxisLabels}
              inverseTone
              minYRange={120}
            />
          )}
        </div>

        {/* Report-backed sections — these still read from the pre-computed
          MonthInReview report, so they fall back to an empty state when the
          report is missing for the selected month. */}
        {dashboardData.isNotFound ? (
          <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
            No data available for this month.
          </div>
        ) : (
          <>
            <Heading
              level={2}
              className="text-base font-semibold text-foreground"
            >
              Playbook
            </Heading>
            <DashboardPlaybookAttainmentTrendCard
              playbookAttainment={dashboardData.playbookAttainment.data}
              teammates={teammates ?? []}
              isLoading={dashboardData.playbookAttainment.isLoading}
              playbookAssessmentTree={playbook?.assessment_tree}
              sectionsAttainment={dashboardData.sectionsAttainment.data}
              sectionsAttainmentIsLoading={
                dashboardData.sectionsAttainment.isLoading
              }
              selectedSectionId={selectedSectionId}
              onSelectedSectionIdChange={setSelectedSectionId}
              showPeriodDropdown={selectedPeriod === undefined}
              playbookAverageScore={
                dashboardData.playbookAttainment.averageScore
              }
              sectionsAverageScore={
                dashboardData.sectionsAttainment.averageScore
              }
              playbookAverageScoreDelta={
                dashboardData.playbookAttainment.averageScoreDeltaWithPrevious
              }
              sectionsAverageScoreDelta={
                dashboardData.sectionsAttainment.averageScoreDeltaWithPrevious
              }
              onSelectedPeriodChange={(period) =>
                handleCardPeriodChange("playbook", period)
              }
            />
            {selectedPeriod !== undefined &&
              outcomeDefinition &&
              dashboardData.outcomeAttainment.data && (
                <>
                  <Heading
                    level={2}
                    className="text-base font-semibold text-foreground"
                  >
                    {getOutcomeNoun(outcomeDefinition).replace(/^./, (c) =>
                      c.toUpperCase(),
                    )}
                  </Heading>
                  <DashboardOutcomeAttainmentTrendCard
                    outcomeAttainment={dashboardData.outcomeAttainment.data}
                    teammates={teammates ?? []}
                    isLoading={dashboardData.outcomeAttainment.isLoading}
                    showPeriodDropdown={selectedPeriod === undefined}
                    outcomeAverageScore={
                      dashboardData.outcomeAttainment.averageScore
                    }
                    outcomeAverageScoreDelta={
                      dashboardData.outcomeAttainment
                        .averageScoreDeltaWithPrevious
                    }
                    outcomeGreenLabel={outcomeGreenRateLabel}
                    onSelectedPeriodChange={(period) =>
                      handleCardPeriodChange("outcome", period)
                    }
                  />
                </>
              )}
            {dashboardData.highImpactCriteriaIds &&
              dashboardData.highImpactCriteriaIds.length > 0 &&
              playbook?.assessment_tree && (
                <>
                  <Heading
                    level={2}
                    className="text-base font-semibold text-foreground"
                  >
                    High impact criteria
                  </Heading>
                  <div className="grid grid-cols-2 gap-4">
                    {dashboardData.highImpactCriteriaIds.map((criterionId) => {
                      const criterion = findCriterionInSubtree(
                        playbook.assessment_tree,
                        criterionId,
                      );
                      if (!criterion) return null;
                      return (
                        <DashboardCriteriaAttainmentCard
                          key={criterionId}
                          criterionName={criterion.name}
                          criterionId={criterionId}
                          gradingValueType={criterion.grading_value_type}
                          attainment={
                            dashboardData.criteriaAttainment.data?.[criterionId]
                          }
                          averageScore={
                            dashboardData.criteriaAttainment.averageScore?.[
                              criterionId
                            ]
                          }
                          averageScoreDelta={
                            dashboardData.criteriaAttainment
                              .averageScoreDeltaWithPrevious?.[criterionId]
                          }
                          teammates={teammates ?? []}
                          isLoading={dashboardData.criteriaAttainment.isLoading}
                          showPeriodDropdown={selectedPeriod === undefined}
                          onSelectedPeriodChange={(period) =>
                            handleCardPeriodChange(
                              `criteria:${criterionId}`,
                              period,
                            )
                          }
                        />
                      );
                    })}
                  </div>
                </>
              )}
            {dashboardData.customerQuotes &&
              dashboardData.customerQuotes.length > 0 && (
                <>
                  <Heading
                    level={2}
                    className="text-base font-semibold text-foreground"
                  >
                    Highlights
                  </Heading>
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {dashboardData.customerQuotes.map((quote, index) => {
                      const teammate = teammatesMap.get(quote.teammate_id);
                      const teammateName = teammate
                        ? buildTeammateDisplayNames(teammate).primaryDisplayName
                        : quote.teammate_id;
                      // Prefer the grading id (new pattern); fall back to the
                      // tuple URL for legacy quotes without one (redirect resolves it).
                      const gradingUrl = quote.grading_id
                        ? appPaths.playbookCallTranscript.build({
                            playbookId,
                            gradingId: quote.grading_id,
                          })
                        : // Quotes without a grading id link the legacy (teammate, account)
                          // tuple URL, which GradingTupleRedirect resolves to the grading.
                          `/performance/playbooks/${encodeURIComponent(playbookId)}/teammates/${encodeURIComponent(quote.teammate_id)}/accounts/${encodeURIComponent(quote.account_id)}/transcript`;
                      return (
                        <HighlightMomentCard
                          key={`${quote.teammate_id}-${quote.account_id}-${index}`}
                          teammate={teammate}
                          teammateName={teammateName}
                          quote={quote.quote}
                          gradingUrl={gradingUrl}
                        />
                      );
                    })}
                  </div>
                </>
              )}
          </>
        )}
      </PageBody>
    </PageLayout>
  );
};
